import * as React from "react";

/**
 * V2ActionsContext - Provides global action handling for V2 UI
 *
 * Actions supported:
 * - add-holding: Opens the AddHoldingDialog
 * - take-snapshot: Triggers snapshot creation
 *
 * This context allows sidebar quick actions to work across all V2 pages
 */

const V2ActionsContext = React.createContext(null);

export function V2ActionsProvider({ children }) {
  const [isAddHoldingOpen, setIsAddHoldingOpen] = React.useState(false);
  const [isTakingSnapshot, setIsTakingSnapshot] = React.useState(false);

  // Handle actions from sidebar and other global triggers
  const handleAction = React.useCallback((action) => {
    switch (action) {
      case "add-holding":
        setIsAddHoldingOpen(true);
        break;
      case "take-snapshot":
        // Snapshot is handled by SnapshotManagement component
        // We dispatch a custom event that SnapshotManagement can listen to
        window.dispatchEvent(new CustomEvent("v2:take-snapshot"));
        break;
      default:
        console.warn(`Unknown action: ${action}`);
    }
  }, []);

  const value = React.useMemo(
    () => ({
      // Add Holding Dialog state
      isAddHoldingOpen,
      setIsAddHoldingOpen,
      // Snapshot state
      isTakingSnapshot,
      setIsTakingSnapshot,
      // Action handler
      handleAction,
    }),
    [isAddHoldingOpen, isTakingSnapshot, handleAction]
  );

  return (
    <V2ActionsContext.Provider value={value}>
      {children}
    </V2ActionsContext.Provider>
  );
}

export function useV2Actions() {
  const context = React.useContext(V2ActionsContext);
  if (!context) {
    throw new Error("useV2Actions must be used within a V2ActionsProvider");
  }
  return context;
}

export default V2ActionsContext;
